import { useCallback, useRef } from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim';

import Button from '@ingka/button';

import type { FormController } from '../hooks/use-form';
import { getControllerStore } from '../hooks/use-form';
import type { FormStore } from '../store';
import type {
    DividerElementConfig,
    FormButtonConfig,
    FormElementConfig,
    FormErrors,
    FormFieldConfig,
    FormStatus,
    FormValuesForElements,
    SubmitElementConfig,
    TranslateFunction,
} from '../types/form-builder.types';
import { FormFieldRenderer } from './form-field.component';

// --- Props ---

export interface FormBuilderProps<Elements extends readonly FormElementConfig[] = readonly FormElementConfig[]> {
    readonly form: FormController<Elements>;
    readonly onSubmit?: ((values: FormValuesForElements<Elements>) => void | Promise<void>) | undefined;
    readonly onSubmitError?: ((errors: FormErrors) => void) | undefined;
    readonly onSubmitFailure?: ((error: unknown) => void) | undefined;
    readonly scrollToFirstError?: boolean | undefined;
    readonly translate?: TranslateFunction | undefined;
    readonly className?: string | undefined;
    readonly prefix?: string | undefined;
}

// --- Internals ---

const isButtonConfig = (config: FormElementConfig): config is FormButtonConfig =>
    config.type === 'button' || config.type === 'submit';

const isDividerConfig = (config: FormElementConfig): config is DividerElementConfig => config.type === 'divider';

const useElementState = (store: FormStore, elementId: string) => {
    const subscribe = useCallback(
        (callback: () => void) => store.subscribeField(elementId, callback),
        [store, elementId]
    );

    const getSnapshot = useCallback(() => store.getFieldSnapshot(elementId), [store, elementId]);

    return useSyncExternalStore(subscribe, getSnapshot);
};

const useFormStatus = (store: FormStore): FormStatus => {
    const subscribe = useCallback((callback: () => void) => store.subscribeForm(callback), [store]);
    const getSnapshot = useCallback(() => store.getFormSnapshot(), [store]);
    return useSyncExternalStore(subscribe, getSnapshot);
};

const useSubmitInProgress = (store: FormStore): boolean => {
    const subscribe = useCallback((callback: () => void) => store.subscribeForm(callback), [store]);
    const getSnapshot = useCallback(() => store.isSubmitInProgress(), [store]);
    return useSyncExternalStore(subscribe, getSnapshot);
};

interface ConnectedFieldProps {
    readonly store: FormStore;
    readonly fieldConfig: FormFieldConfig;
    readonly translate: TranslateFunction;
    readonly domIdPrefix: string;
    readonly prefix?: string | undefined;
}

const ConnectedField = ({ store, fieldConfig, translate, domIdPrefix, prefix }: ConnectedFieldProps) => {
    const fieldState = useElementState(store, fieldConfig.id);

    if (!fieldState.rendered || fieldState.hidden) {
        return null;
    }

    return (
        <div data-element-id={fieldConfig.id} data-element-type={fieldConfig.type}>
            <FormFieldRenderer
                fieldConfig={fieldConfig}
                fieldState={fieldState}
                translate={translate}
                domIdPrefix={domIdPrefix}
                prefix={prefix}
                onChange={(value) => {
                    store.setFieldValue(fieldConfig.id, value);
                }}
                onBlur={() => {
                    store.handleBlur(fieldConfig.id);
                }}
            />
        </div>
    );
};

interface ConnectedButtonProps {
    readonly store: FormStore;
    readonly buttonConfig: FormButtonConfig;
    readonly translate: TranslateFunction;
    readonly prefix?: string | undefined;
}

const isSubmitConfig = (config: FormButtonConfig): config is SubmitElementConfig => config.type === 'submit';

const ConnectedButton = ({ store, buttonConfig, translate, prefix }: ConnectedButtonProps) => {
    const state = useElementState(store, buttonConfig.id);
    const formStatus = useFormStatus(store);
    const submitInProgress = useSubmitInProgress(store);

    if (!state.rendered || state.hidden) {
        return null;
    }

    const isSubmit = isSubmitConfig(buttonConfig);
    const isLoading = isSubmit && submitInProgress;
    const label =
        isSubmit && formStatus.isSubmitting && buttonConfig.submittingLabel
            ? translate(buttonConfig.submittingLabel)
            : translate(buttonConfig.label);

    const onClickProps =
        buttonConfig.type === 'button'
            ? {
                  onClick: () => {
                      void store.executeAction(buttonConfig.action);
                  },
              }
            : {};

    const prefixProp = prefix ? { prefix } : {};

    return (
        <div data-element-id={buttonConfig.id} data-element-type={buttonConfig.type}>
            <Button
                {...prefixProp}
                type={buttonConfig.buttonVariant ?? 'secondary'}
                htmlType={isSubmit ? 'submit' : 'button'}
                text={label}
                disabled={state.disabled || isLoading}
                loading={isLoading}
                {...onClickProps}
            />
        </div>
    );
};

interface ElementGroup {
    readonly group: string | undefined;
    readonly elements: FormElementConfig[];
}

interface GroupedElementGroup {
    readonly group: string;
    readonly elements: FormElementConfig[];
}

const isGroupedElementGroup = (elementGroup: ElementGroup): elementGroup is GroupedElementGroup =>
    elementGroup.group !== undefined;

const useGroupVisibility = (store: FormStore, elementGroup: ElementGroup): boolean => {
    const subscribe = useCallback((callback: () => void) => store.subscribeForm(callback), [store]);
    const getSnapshot = useCallback(
        () =>
            elementGroup.elements.some((element) => {
                const state = store.getFieldSnapshot(element.id);
                return state.rendered && !state.hidden;
            }),
        [store, elementGroup]
    );

    return useSyncExternalStore(subscribe, getSnapshot);
};

const groupConsecutiveElements = (elements: readonly FormElementConfig[]): readonly ElementGroup[] => {
    const groups: ElementGroup[] = [];

    for (const element of elements) {
        const lastGroup = groups[groups.length - 1];

        if (lastGroup?.group !== undefined && lastGroup.group === element.group) {
            lastGroup.elements.push(element);
        } else {
            groups.push({ group: element.group, elements: [element] });
        }
    }

    return groups;
};

const ConnectedDivider = ({
    store,
    dividerConfig,
}: {
    readonly store: FormStore;
    readonly dividerConfig: DividerElementConfig;
}) => {
    const state = useElementState(store, dividerConfig.id);

    if (!state.rendered || state.hidden) {
        return null;
    }

    return <hr data-element-id={dividerConfig.id} data-element-type="divider" />;
};

const ConnectedGroup = ({
    store,
    elementGroup,
    renderElement,
}: {
    readonly store: FormStore;
    readonly elementGroup: GroupedElementGroup;
    readonly renderElement: (element: FormElementConfig) => React.JSX.Element | readonly React.JSX.Element[];
}) => {
    const hasVisibleElement = useGroupVisibility(store, elementGroup);
    if (!hasVisibleElement) {
        return null;
    }

    return (
        <div
            key={`${elementGroup.group}-${elementGroup.elements[0]?.id ?? 'group'}`}
            className="form-builder__group"
            data-form-builder-group={elementGroup.group}
        >
            {elementGroup.elements.map(renderElement)}
        </div>
    );
};

// --- Public API ---

interface FormBuilderInnerProps<
    Elements extends readonly FormElementConfig[] = readonly FormElementConfig[],
> extends FormBuilderProps<Elements> {
    readonly form: FormController<Elements>;
}

const FormBuilderInner = <Elements extends readonly FormElementConfig[]>({
    form,
    onSubmit,
    onSubmitError,
    onSubmitFailure,
    scrollToFirstError = true,
    translate = (key) => key,
    className,
    prefix,
}: FormBuilderInnerProps<Elements>): React.JSX.Element => {
    const store = getControllerStore(form);
    const { elements, id } = store.config;
    const groups = groupConsecutiveElements(elements);
    const formRef = useRef<HTMLFormElement | null>(null);

    const renderElement = (element: FormElementConfig) => {
        if (isDividerConfig(element)) {
            return <ConnectedDivider key={element.id} store={store} dividerConfig={element} />;
        }
        if (isButtonConfig(element)) {
            return (
                <ConnectedButton
                    key={element.id}
                    store={store}
                    buttonConfig={element}
                    translate={translate}
                    prefix={prefix}
                />
            );
        }
        return (
            <ConnectedField
                key={element.id}
                store={store}
                fieldConfig={element}
                translate={translate}
                domIdPrefix={id}
                prefix={prefix}
            />
        );
    };

    return (
        <form
            id={id}
            ref={formRef}
            className={className}
            data-form-builder={id}
            noValidate
            onSubmit={(event) => {
                event.preventDefault();
                void form.submit({ onSubmit, onSubmitError, onSubmitFailure }).then((result) => {
                    if (!result.ok && Object.keys(result.errors).length > 0 && scrollToFirstError) {
                        const firstErrorFieldId = elements.find((el) => result.errors[el.id] !== undefined)?.id;
                        if (firstErrorFieldId) {
                            formRef.current
                                ?.querySelector(`[data-element-id="${firstErrorFieldId}"]`)
                                ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                        }
                    }
                });
            }}
        >
            {groups.map((group) => {
                if (isGroupedElementGroup(group)) {
                    return (
                        <ConnectedGroup
                            key={`${group.group}-${group.elements[0]?.id ?? 'group'}`}
                            store={store}
                            elementGroup={group}
                            renderElement={renderElement}
                        />
                    );
                }

                return group.elements.map(renderElement);
            })}
        </form>
    );
};

export function FormBuilder<Elements extends readonly FormElementConfig[]>(
    props: FormBuilderProps<Elements>
): React.JSX.Element {
    return <FormBuilderInner<Elements> {...props} />;
}
